import axios, {AxiosError} from 'axios'
import {
  chatConversationsUrl,
  chatConversationWithAdminUrl,
  chatConversationWithUserUrl,
  chatStartNewConversationUrl,
  chatConversationMessagesUrl,
  chatRoomMessagesUrl,
  chatSendMessageUrl,
  chatMarkConversationReadUrl,
  chatDeleteConversationUrl,
  chatAiConversationUrl,
  chatAiMessageUrl,
} from '../redux/api'
import type {ChatConversation, ChatMessage} from './chatSupportTypes'

/** Normalize conversation: ensure both conversationId and _id are set (backend sends conversationId). */
function normalizeConversation(conv: ChatConversation): ChatConversation {
  const id = conv.conversationId ?? conv._id
  if (!id) return conv
  return {
    ...conv,
    conversationId: conv.conversationId ?? id,
    _id: conv._id ?? id,
  }
}

/** Normalize conversation array. */
function normalizeConversations(convs: ChatConversation[]): ChatConversation[] {
  return convs.map(normalizeConversation)
}

/** True when the error is 404/501 (not implemented), 500 (server error), or 503 (AI not configured) — chat unavailable. */
export function isChatApiUnavailable(e: unknown): boolean {
  const status = (e as AxiosError)?.response?.status
  return status === 404 || status === 501 || status === 500 || status === 503
}

/** Error thrown when chat API is unavailable (404/501/500). Hook can set chatAvailable = false. */
export class ChatApiUnavailableError extends Error {
  status?: number
  /** Server error message (en/fr or message) when status is 500. */
  serverMessage?: string
  constructor(status?: number, serverMessage?: string) {
    const msg = serverMessage || (status === 500 ? 'Chat service is temporarily unavailable.' : 'Chat is not available yet.')
    super(msg)
    this.name = 'ChatApiUnavailableError'
    this.status = status
    this.serverMessage = serverMessage
  }
}

/**
 * GET /api/chat/ai/conversation — get or create AI conversation (non-admin only).
 * Returns same conversation shape. 403 for admin. 503 if not configured (no bot user, or OPENROUTER_API_KEY missing/invalid).
 * Per docs/frontend/CHAT-AI-OPENROUTER.md.
 */
export async function getOrCreateAiConversation(): Promise<ChatConversation> {
  try {
    const res = await axios.get<{ conversation: ChatConversation }>(chatAiConversationUrl)
    return normalizeConversation(res.data.conversation)
  } catch (e) {
    const status = (e as AxiosError)?.response?.status
    const data = (e as AxiosError)?.response?.data as { message?: string; en?: string; fr?: string } | undefined
    if (status === 503) {
      throw new ChatApiUnavailableError(503, data?.message ?? data?.en ?? data?.fr ?? 'AI chat is not configured.')
    }
    if (status === 403) throw new ChatApiUnavailableError(403)
    if (isChatApiUnavailable(e)) throw new ChatApiUnavailableError(status)
    throw e
  }
}

/** Response from POST /api/chat/ai/message (per CHAT-AI-OPENROUTER.md). aiMessage may be omitted if backend sends it via Socket only. */
export interface SendAiMessageResponse {
  userMessage: ChatMessage
  /** May be null/undefined if backend sends AI reply asynchronously via Socket.IO (ai:reply). */
  aiMessage?: ChatMessage | null
  conversationId: string
  /** Some backends may use "message" for the AI reply. */
  message?: ChatMessage
}

/**
 * POST /api/chat/ai/message — send message to AI; body { conversationId, content }.
 * Returns { userMessage, aiMessage, conversationId }. Backend saves both messages before responding; may also emit via Socket.IO.
 * 503 if OPENROUTER_API_KEY missing/invalid or bot user missing. Per docs/frontend/CHAT-AI-OPENROUTER.md.
 * Accepts top-level or nested { data: { userMessage, aiMessage, conversationId } }.
 */
export async function sendAiMessage(conversationId: string, content: string): Promise<SendAiMessageResponse> {
  const res = await axios.post<SendAiMessageResponse & { data?: SendAiMessageResponse }>(chatAiMessageUrl, {
    conversationId,
    content,
  })
  const raw = res.data
  if (!raw) throw new Error('Empty response from AI chat')
  const data = raw.data ?? raw
  return {
    userMessage: data.userMessage,
    aiMessage: data.aiMessage ?? data.message,
    conversationId: data.conversationId ?? conversationId,
  } as SendAiMessageResponse
}

/** GET /api/chat/conversations — list my conversations (admin: all; user: one with admin). */
export async function fetchConversations(): Promise<ChatConversation[]> {
  try {
    const res = await axios.get<{ conversations: ChatConversation[] }>(chatConversationsUrl)
    const convs = res.data?.conversations ?? []
    return normalizeConversations(convs)
  } catch (e) {
    if (isChatApiUnavailable(e)) {
      throw new ChatApiUnavailableError()
    }
    throw e
  }
}

/**
 * POST /api/chat/conversations/with-admin — get or create conversation with admin (user only).
 * Backend returns existing or new conversation.
 */
export async function getOrCreateConversationWithAdmin(): Promise<ChatConversation> {
  try {
    const res = await axios.post<{ conversation: ChatConversation }>(chatConversationWithAdminUrl)
    return normalizeConversation(res.data.conversation)
  } catch (e) {
    const status = (e as AxiosError)?.response?.status
    if (isChatApiUnavailable(e)) throw new ChatApiUnavailableError(status)
    throw e
  }
}

/**
 * POST /api/chat/conversations/with-user/:userId — get or create conversation with a specific user (admin only).
 * Backend returns existing or new conversation between admin and the specified user.
 */
export async function getOrCreateConversationWithUser(userId: string): Promise<ChatConversation> {
  try {
    const res = await axios.post<{ conversation: ChatConversation }>(chatConversationWithUserUrl(userId))
    return normalizeConversation(res.data.conversation)
  } catch (e) {
    const status = (e as AxiosError)?.response?.status
    if (isChatApiUnavailable(e)) throw new ChatApiUnavailableError(status)
    throw e
  }
}

/**
 * POST /api/chat/conversations/start-new — start a new conversation with admin (non-admin only).
 * Archives current active conversation (status: "archived", archivedAt set) and creates a new active one.
 * Messages are kept for history. Returns the new conversation (conversationId, roomId, status: "active").
 */
export async function startNewConversationWithAdmin(): Promise<ChatConversation> {
  try {
    const res = await axios.post<{ conversation?: ChatConversation } | ChatConversation>(chatStartNewConversationUrl)
    // Backend may send { conversation } or the conversation object directly
    const conversation = typeof res.data === 'object' && res.data !== null && 'conversation' in res.data
      ? (res.data as { conversation: ChatConversation }).conversation
      : (res.data as ChatConversation)
    return normalizeConversation(conversation)
  } catch (e) {
    const axiosErr = e as AxiosError<{ message?: string; en?: string; fr?: string }>
    const status = axiosErr?.response?.status
    const errorData = axiosErr?.response?.data
    // Handle MongoDB duplicate key error (E11000)
    if (errorData && typeof errorData === 'object' && ('en' in errorData || 'fr' in errorData)) {
      const errorMsg = errorData.en || errorData.fr || ''
      if (errorMsg.includes('E11000') || errorMsg.includes('duplicate key')) {
        throw new ChatApiUnavailableError(500, errorMsg)
      }
    }
    if (status === 403) throw new ChatApiUnavailableError(403)
    if (status === 404) throw new ChatApiUnavailableError(404)
    if (status === 500) {
      const serverMsg = errorData?.message || errorData?.en || errorData?.fr
      throw new ChatApiUnavailableError(500, serverMsg)
    }
    if (isChatApiUnavailable(e)) throw new ChatApiUnavailableError(status)
    throw e
  }
}

/**
 * DELETE /api/chat/conversations/:id — delete conversation and all its messages (admin only).
 * Returns { deleted: true }. 403 if not admin; 404 if not found; 400 if invalid id.
 */
export async function deleteConversation(conversationId: string): Promise<{ deleted: boolean }> {
  const res = await axios.delete<{ deleted: boolean }>(chatDeleteConversationUrl(conversationId))
  return res.data
}

/** Query params for GET /api/chat/conversations/:id/messages (optional pagination). */
export interface FetchMessagesParams {
  limit?: number
  before?: string
}

/** GET /api/chat/conversations/:id/messages — get messages for a conversation. */
export async function fetchMessages(
  conversationId: string,
  params?: FetchMessagesParams
): Promise<ChatMessage[]> {
  try {
    const qs = new URLSearchParams()
    if (params?.limit != null) qs.set('limit', String(params.limit))
    if (params?.before) qs.set('before', params.before)
    const query = qs.toString()
    const url = query
      ? `${chatConversationMessagesUrl(conversationId)}?${query}`
      : chatConversationMessagesUrl(conversationId)
    const res = await axios.get<{ messages: ChatMessage[] }>(url)
    return res.data?.messages ?? []
  } catch (e) {
    const status = (e as AxiosError)?.response?.status
    if (status === 403 || isChatApiUnavailable(e)) return []
    throw e
  }
}

/** GET /api/chat/rooms/:roomId/messages — get messages by roomId (e.g. after refresh when only roomId available). */
export async function fetchMessagesByRoom(
  roomId: string,
  params?: FetchMessagesParams
): Promise<ChatMessage[]> {
  try {
    const qs = new URLSearchParams()
    if (params?.limit != null) qs.set('limit', String(params.limit))
    if (params?.before) qs.set('before', params.before)
    const query = qs.toString()
    const url = query
      ? `${chatRoomMessagesUrl(roomId)}?${query}`
      : chatRoomMessagesUrl(roomId)
    const res = await axios.get<{ messages: ChatMessage[]; roomId?: string }>(url)
    return res.data?.messages ?? []
  } catch (e) {
    const status = (e as AxiosError)?.response?.status
    if (status === 403 || isChatApiUnavailable(e)) return []
    throw e
  }
}


/** POST /api/chat/messages — send a message. */
export async function sendMessage(
  conversationId: string,
  content: string
): Promise<ChatMessage> {
  try {
    const res = await axios.post<{ message: ChatMessage }>(chatSendMessageUrl, {
      conversationId,
      content,
    })
    return res.data.message
  } catch (e) {
    const status = (e as AxiosError)?.response?.status
    if (status === 403) throw new ChatApiUnavailableError(403)
    if (isChatApiUnavailable(e)) throw new ChatApiUnavailableError(status)
    throw e
  }
}

/** POST /api/chat/conversations/:id/read — mark conversation as read. */
export async function markConversationRead(conversationId: string): Promise<void> {
  try {
    await axios.post(chatMarkConversationReadUrl(conversationId))
  } catch (e) {
    const status = (e as AxiosError)?.response?.status
    if (status === 403 || isChatApiUnavailable(e)) return
    throw e
  }
}
